import { Component, NgZone, OnDestroy, OnInit, HostListener, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';

//angular material
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';


//bootstrap
import { NgbPaginationModule, NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';



@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, MatMenuModule, MatButtonModule, NgbPaginationModule, NgbAlertModule, NgbAlert, NgbCollapseModule],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss'
})
export class NavbarComponent implements OnInit, OnDestroy {
  isCollapsed = true;
  isScrolled = false;
  scrollProgress = 0;
  private isBrowser: boolean;
  private scrollRaf = 0;
  private scrollProgressListener: (() => void) | null = null;
  private scrollProgressCleanups: Array<() => void> = [];

  menuItems = [
    { text: 'Inicio', route: '/inicio', icon: 'bi bi-house' },
    { text: 'Acerca', route: '/acerca', icon: 'bi bi-info-circle' },
    { text: 'Servicios', route: '/servicios', icon: 'bi bi-gear' },
    { text: 'Clientes', route: '/clientes', icon: 'bi bi-people' }
  ];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.isBrowser) {
      this.scheduleScrollProgressUpdate();
    }
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.setMobileMenuLock(false);
      this.bindScrollProgressListeners();
      this.scheduleScrollProgressUpdate();
      window.setTimeout(() => this.scheduleScrollProgressUpdate(), 120);
      window.setTimeout(() => this.scheduleScrollProgressUpdate(), 600);
    }
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    if (!this.isBrowser) return;
    const isDesktop = window.innerWidth > 991.98;
    if (isDesktop) {
      this.isCollapsed = true;
      this.setMobileMenuLock(false);
      return;
    }
    if (this.isCollapsed) {
      this.setMobileMenuLock(false);
    }
  }

  ngOnDestroy() {
    this.setMobileMenuLock(false);
    this.unbindScrollProgressListeners();
  }

  toggleMenu() {
    this.isCollapsed = !this.isCollapsed;
    this.setMobileMenuLock(!this.isCollapsed);
  }

  closeMenu() {
    this.isCollapsed = true;
    this.setMobileMenuLock(false);
  }

  private bindScrollProgressListeners(): void {
    if (!this.isBrowser || this.scrollProgressListener) return;

    this.scrollProgressListener = () => this.scheduleScrollProgressUpdate();

    const options: AddEventListenerOptions = { passive: true };
    window.addEventListener('scroll', this.scrollProgressListener, options);
    document.addEventListener('scroll', this.scrollProgressListener, options);
    window.addEventListener('resize', this.scrollProgressListener, options);
    window.addEventListener('orientationchange', this.scrollProgressListener, options);

    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('scroll', this.scrollProgressListener, options);
      viewport.addEventListener('resize', this.scrollProgressListener, options);
      this.scrollProgressCleanups.push(() => {
        viewport.removeEventListener('scroll', this.scrollProgressListener!, options);
        viewport.removeEventListener('resize', this.scrollProgressListener!, options);
      });
    }

    this.scrollProgressCleanups.push(() => {
      window.removeEventListener('scroll', this.scrollProgressListener!, options);
      document.removeEventListener('scroll', this.scrollProgressListener!, options);
      window.removeEventListener('resize', this.scrollProgressListener!, options);
      window.removeEventListener('orientationchange', this.scrollProgressListener!, options);
      this.scrollProgressListener = null;
    });
  }

  private unbindScrollProgressListeners(): void {
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = 0;
    }
    this.scrollProgressCleanups.forEach((cleanup) => cleanup());
    this.scrollProgressCleanups = [];
    this.scrollProgressListener = null;
  }

  private scheduleScrollProgressUpdate(): void {
    if (!this.isBrowser || this.scrollRaf) return;

    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      this.ngZone.run(() => this.updateScrollProgress());
    });
  }

  private updateScrollProgress() {
    if (!this.isBrowser) return;

    const scrollTop = Math.max(
      window.scrollY || 0,
      window.pageYOffset || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0
    );
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const scrollHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight
    );
    const docHeight = scrollHeight - viewportHeight;

    this.isScrolled = scrollTop > 50;
    this.scrollProgress = docHeight > 0
      ? Math.min(100, Math.max(0, (scrollTop / docHeight) * 100))
      : 0;
  }

  private setMobileMenuLock(shouldLock: boolean) {
    if (!this.isBrowser) return;
    const canLock = window.matchMedia('(max-width: 991.98px)').matches;
    const lock = shouldLock && canLock;
    document.body.classList.toggle('mobile-menu-open', lock);
    document.documentElement.classList.toggle('mobile-menu-open', lock);
  }
}
